// Shared math helpers for gameplay flows (training, vs_robot, vs_human).
// Extracted from GamePlay.jsx so hooks can consume the same logic.

// Canonical SUPERT letters. Mirrored locally (instead of imported from
// @/components/gameplay/parts) to avoid pulling React/JSX deps into Jest
// test runners that don't resolve the `@/` alias.
const SUPERT = ["S", "U", "P", "E", "R", "T"];

/**
 * Returns true when `progress` (an array of letter codes + "*" wildcards)
 * already covers a full SUPERT cycle (or, for "sem_sorte" mode, when the
 * raw letter count covers `supertCount` complete sets of six).
 */
export function isWinningProgress(progress, mode, supertCount) {
  const letters = progress.filter((p) => p !== "*");
  const stars = progress.filter((p) => p === "*").length;
  if (mode === "sem_sorte") return Math.floor(letters.length / 6) >= supertCount;
  const required = new Set(SUPERT);
  const have = new Set(letters);
  const missing = [...required].filter((l) => !have.has(l)).length;
  return missing <= stars;
}

/**
 * Client-side mirror of backend `supert_award` in `/app/backend/game_logic.py`.
 * Mirrors the same probability distribution so the UI can advance the round
 * IMMEDIATELY without a network roundtrip — eliminating the 100–500 ms
 * latency that VS HUMAN players perceive as "the click doesn't respond".
 *
 * Returns: { item: "S"|"U"|"P"|"E"|"R"|"T"|"*", progress: string[],
 *            was_repeat: boolean }.
 *
 * IMPORTANT: this is intentionally non-deterministic (Math.random) and not
 * authoritative — the server is still the source of truth for permanent
 * stats. For VS HUMAN, where each player's progress is self-reported via
 * the WebSocket anyway, the local award is functionally equivalent.
 */
export function localSupertAward({
  progress = [],
  mode = "normal",
  repeats_so_far = 0,
  max_repeated_letters = 0,
} = {}) {
  const missing = SUPERT.filter((l) => !progress.includes(l));

  // sem_sorte: pick uniformly from missing letters, no stars/repeats
  if (mode === "sem_sorte") {
    const pool = missing.length ? missing : SUPERT;
    const pick = pool[Math.floor(Math.random() * pool.length)];
    return {
      item: pick,
      progress: missing.length ? [...progress, pick] : [...progress, pick],
      was_repeat: false,
    };
  }

  // normal / sorte_ajustada — weighted: S=U=P=E=R=T=7, *=12 (total 54)
  const weights = [
    ["S", 7], ["U", 7], ["P", 7], ["E", 7], ["R", 7], ["T", 7],
    ["*", 12],
  ];
  const total = weights.reduce((acc, [, w]) => acc + w, 0);
  let r = Math.random() * total;
  let pick = "*";
  for (const [item, w] of weights) {
    r -= w;
    if (r < 0) { pick = item; break; }
  }

  // sorte_ajustada: if pick is a repeated letter and cap reached, re-roll to missing
  if (mode === "sorte_ajustada" && pick !== "*" && progress.includes(pick)) {
    if (repeats_so_far >= max_repeated_letters && missing.length) {
      pick = missing[Math.floor(Math.random() * missing.length)];
    }
  }

  const isRepeat =
    pick !== "*" && progress.includes(pick) && (mode === "normal" || mode === "sorte_ajustada");
  const nextProgress = isRepeat ? [...progress] : [...progress, pick];
  return { item: pick, progress: nextProgress, was_repeat: isRepeat };
}

